import React from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { Badge, withStyles } from '@material-ui/core';
import { useQuery } from '@tanstack/react-query';

import { adminListOffersCountApi } from '../../api/offers/adminListOffersCountApi';
import { supportCountReservationsForReviewApi } from '../../api/reservations/supportCountReservationsForReviewApi';
import { adminGetUnreadUserComplaintCountApi } from '../../api/supports/adminGetUnreadUserComplaintCountApi';
import { OfferStatus, ReservsationsSupportListType } from '../../graphql/API';
import { useChatSupportTicketCounts } from '../../hooks/reactQuery/chat/useChatSupportTicketCounts';
import { useAdminRole } from '../../hooks/useAdminRole';
import { icons } from '../../themes/icons';
import { COMPLAINT_TYPES } from '../Tables/SupportsTable/types';

interface MenuItem {
  label: string;
  route: string;
  icon: React.FC;
  exact?: boolean;
  count?: number;
}

const StyledBadge = withStyles((theme) => ({
  badge: {
    right: 16,
    top: 0,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    backgroundColor: 'rgba(241, 13, 0, 1)',
  },
}))(Badge);

const menuVenue = (venueId?: string) => {
  const menueItems: MenuItem[] = [
    { label: 'Businesses', route: '/businesses', icon: icons.StorefrontIcon, count: 0 },
    { label: 'Offers', route: '/offers', icon: icons.LocalOfferIcon, count: 0 },
    { label: 'Reservations', route: '/reservations', icon: icons.EventAvailableIcon, count: 0 },
  ];

  return menueItems;
};

const menuItemsSupport = (
  supportCount?: number,
  unreadChatCount?: number,
  totalFastCheckPending?: number,
): MenuItem[] => [
  { label: 'Dashboard', route: '/', icon: icons.DashboardIcon, exact: true, count: 0 },
  {
    label: 'Fast Check',
    route: '/fast-check-reservations',
    icon: icons.FastForward,
    count: totalFastCheckPending,
  },
  {
    label: 'Profile Updates',
    route: '/profile-updates',
    icon: icons.SupportIcon,
    count: supportCount,
  },
  { label: 'Chat', route: '/chats', icon: icons.ChatIcon, count: unreadChatCount },
];

const menuItemsSocial: MenuItem[] = [
  { label: 'Content', route: '/content', icon: icons.CollectionsIcon, count: 0 },
];

const menuItemsFastCheck = (totalFastCheckPending?: number): MenuItem[] => [
  { label: 'Dashboard', route: '/', icon: icons.DashboardIcon, exact: true, count: 0 },
  {
    label: 'Fast Check',
    route: '/fast-check-reservations',
    icon: icons.FastForward,
    count: totalFastCheckPending,
  },
];

const menuItemsCityManager = ({ unreadChatCount }: { unreadChatCount?: number }): MenuItem[] => [
  { label: 'Dashboard', route: '/', icon: icons.DashboardIcon, exact: true, count: 0 },
  { label: 'Referrals', route: '/referrals', icon: icons.ArrowUpwardIcon },
  { label: 'Chat', route: '/chats', icon: icons.ChatIcon, count: unreadChatCount },
  {
    label: 'Business Applications',
    route: '/business-applications',
    icon: icons.StorefrontIcon,
    exact: true,
    count: 0,
  },
  {
    label: 'Offer Applications',
    route: '/offer-applications',
    icon: icons.LocalOfferIcon,
    exact: true,
    count: 0,
  },
  { label: 'Markets', route: '/markets', icon: icons.LocationCityIcon, count: 0 },
  { label: 'Businesses', route: '/businesses', icon: icons.StorefrontIcon, count: 0 },
  { label: 'Offers', route: '/offers', icon: icons.LocalOfferIcon, count: 0 },
];

const menuItemsSuperAdmin = ({
  supportCount,
  offerPendingCount,
  unreadChatCount,
  totalFastCheckPending,
}: {
  supportCount?: number;
  offerPendingCount?: number;
  unreadChatCount?: number;
  totalFastCheckPending?: number;
}): MenuItem[] => [
  { label: 'Dashboard', route: '/', icon: icons.DashboardIcon, exact: true, count: 0 },
  {
    label: 'Profile Updates',
    route: '/profile-updates',
    icon: icons.SupportIcon,
    count: supportCount,
  },
  { label: 'Chat', route: '/chats', icon: icons.ChatIcon, count: unreadChatCount },
  {
    label: 'Model Applications',
    route: '/user-applications',
    icon: icons.PeopleIcon,
    exact: true,
    count: 0,
  },
  {
    label: 'Business Applications',
    route: '/business-applications',
    icon: icons.StorefrontIcon,
    exact: true,
    count: 0,
  },
  {
    label: 'Offer Applications',
    route: '/offer-applications',
    icon: icons.LocalOfferIcon,
    exact: true,
    count: offerPendingCount,
  },
  { label: 'Markets', route: '/markets', icon: icons.LocationCityIcon, count: 0 },
  { label: 'Models', route: '/users', icon: icons.PeopleIcon, count: 0 },
  { label: 'Businesses', route: '/businesses', icon: icons.StorefrontIcon, count: 0 },
  { label: 'Agencies', route: '/agencies', icon: icons.SheildIcon, count: 0 },
  { label: 'Collabs', route: '/collabs', icon: icons.EmailIcon, count: 0 },
  { label: 'Scouting', route: '/scouting', icon: icons.ScoutingIcon, count: 0 },
  { label: 'Offers', route: '/offers', icon: icons.LocalOfferIcon, count: 0 },
  { label: 'Reservations', route: '/reservations', icon: icons.EventAvailableIcon, count: 0 },
  {
    label: 'Fast Check',
    route: '/fast-check-reservations',
    icon: icons.FastForward,
    count: totalFastCheckPending,
  },
  { label: 'No Shows', route: '/no-shows', icon: icons.CancelIcon, count: 0 },
  { label: 'Penalties', route: '/penalties', icon: icons.WarningIcon, count: 0 },
  { label: 'Suspensions', route: '/suspensions', icon: icons.BlockIcon, count: 0 },
  { label: 'Content', route: '/content', icon: icons.CollectionsIcon, count: 0 },
  { label: 'Campaigns', route: '/campaigns', icon: icons.NotificationsActiveIcon, count: 0 },
  { label: 'Staff', route: '/staff', icon: icons.FaceIcon, count: 0 },
  { label: 'Referrals', route: '/referrals', icon: icons.ArrowUpwardIcon, count: 0 },
  { label: 'Logs', route: '/logs', icon: icons.ViewStreamIcon, count: 0 },
  {
    label: 'Terms & Conditions',
    route: '/terms-and-conditions',
    icon: icons.AssignmentIcon,
    count: 0,
  },
  { label: 'Search Businesses', route: '/search-businesses', icon: icons.SearchIconV2, count: 0 },
];

const menuItemsAdmin = ({
  supportCount,
  offerPendingCount,
  unreadChatCount,
  totalFastCheckPending,
}: {
  supportCount?: number;
  offerPendingCount?: number;
  unreadChatCount?: number;
  totalFastCheckPending?: number;
}): MenuItem[] => [
  { label: 'Dashboard', route: '/', icon: icons.DashboardIcon, exact: true, count: 0 },
  {
    label: 'Profile Updates',
    route: '/profile-updates',
    icon: icons.SupportIcon,
    count: supportCount,
  },
  { label: 'Chat', route: '/chats', icon: icons.ChatIcon, count: unreadChatCount },
  {
    label: 'Model Applications',
    route: '/user-applications',
    icon: icons.PeopleIcon,
    exact: true,
    count: 0,
  },
  {
    label: 'Business Applications',
    route: '/business-applications',
    icon: icons.StorefrontIcon,
    exact: true,
    count: 0,
  },
  {
    label: 'Offer Applications',
    route: '/offer-applications',
    icon: icons.LocalOfferIcon,
    exact: true,
    count: offerPendingCount,
  },
  { label: 'Markets', route: '/markets', icon: icons.LocationCityIcon, count: 0 },
  { label: 'Models', route: '/users', icon: icons.PeopleIcon, count: 0 },
  { label: 'Businesses', route: '/businesses', icon: icons.StorefrontIcon, count: 0 },
  { label: 'Agencies', route: '/agencies', icon: icons.SheildIcon, count: 0 },
  { label: 'Collabs', route: '/collabs', icon: icons.EmailIcon, count: 0 },
  { label: 'Scouting', route: '/scouting', icon: icons.ScoutingIcon, count: 0 },
  { label: 'Offers', route: '/offers', icon: icons.LocalOfferIcon, count: 0 },
  { label: 'Reservations', route: '/reservations', icon: icons.EventAvailableIcon, count: 0 },
  {
    label: 'Fast Check',
    route: '/fast-check-reservations',
    icon: icons.FastForward,
    count: totalFastCheckPending,
  },
  { label: 'No Shows', route: '/no-shows', icon: icons.CancelIcon, count: 0 },
  { label: 'Penalties', route: '/penalties', icon: icons.WarningIcon, count: 0 },
  { label: 'Suspensions', route: '/suspensions', icon: icons.BlockIcon, count: 0 },
  { label: 'Content', route: '/content', icon: icons.CollectionsIcon, count: 0 },
  { label: 'Referrals', route: '/referrals', icon: icons.ArrowUpwardIcon, count: 0 },
  { label: 'Search Businesses', route: '/search-businesses', icon: icons.SearchIconV2, count: 0 },
];

const MobileSidebar = () => {
  const {
    isSupport,
    isCityManager,
    isVenue,
    venueId,
    isSocial,
    isAdmin,
    isFastCheck,
    isSuperAdmin,
  } = useAdminRole();

  const { data: supportUnreadCount } = useQuery({
    queryKey: ['list-support', 'support-count', 'unread-count'],
    queryFn: () => adminGetUnreadUserComplaintCountApi({ types: COMPLAINT_TYPES }),
    enabled: isAdmin,
  });

  const { userSupportCount, venueSupportCount } = useChatSupportTicketCounts({
    type: '',
  });

  const { data: offersCount } = useQuery({
    queryKey: ['offers-pending-count'],
    queryFn: () =>
      adminListOffersCountApi({
        status: OfferStatus.pending,
        search: '',
        sortBy: 'createdAt',
        sortDirection: 'desc',
        marketID: '',
        category: null,
      }),
    enabled: isAdmin,
  });

  const { data: fastChecksCount } = useQuery({
    queryKey: ['fast-check-count-mobile'],
    queryFn: () =>
      supportCountReservationsForReviewApi({
        type: ReservsationsSupportListType.pending,
      }),
    enabled: !isSocial,
  });

  const totalFastCheckPending = React.useMemo(
    () =>
      fastChecksCount?.reduce((total, item) => {
        return total + (item.count ?? 0);
      }, 0),
    [fastChecksCount],
  );

  const menuVenueMemo = React.useMemo(() => {
    return menuVenue(venueId);
  }, [venueId]);

  const menuItems = isSupport
    ? menuItemsSupport(
        supportUnreadCount,
        (userSupportCount?.open || 0) + (venueSupportCount?.open || 0),
        totalFastCheckPending,
      )
    : isSocial
    ? menuItemsSocial
    : isCityManager
    ? menuItemsCityManager({
        unreadChatCount: (userSupportCount?.open || 0) + (venueSupportCount?.open || 0),
      })
    : isFastCheck
    ? menuItemsFastCheck(totalFastCheckPending)
    : isVenue
    ? menuVenueMemo
    : isSuperAdmin
    ? menuItemsSuperAdmin({
        supportCount: supportUnreadCount,
        offerPendingCount: offersCount ? offersCount['totalPending'] : undefined,
        unreadChatCount: (userSupportCount?.open || 0) + (venueSupportCount?.open || 0),
        totalFastCheckPending: totalFastCheckPending,
      })
    : menuItemsAdmin({
        supportCount: supportUnreadCount,
        offerPendingCount: offersCount ? offersCount['totalPending'] : undefined,
        unreadChatCount: (userSupportCount?.open || 0) + (venueSupportCount?.open || 0),
        totalFastCheckPending: totalFastCheckPending,
      });

  return (
    <SidebarContainer>
      {menuItems.map((item, index) => (
        <SidebarItem key={index} to={item.route} activeClassName="active" exact={item.exact}>
          <StyledBadge badgeContent={item.count} color="secondary">
            <item.icon />
          </StyledBadge>
          <Label>{item.label}</Label>
        </SidebarItem>
      ))}
    </SidebarContainer>
  );
};

export default MobileSidebar;

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SidebarItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  padding-left: 1rem;
  color: inherit;
  text-decoration: none;
  position: relative;

  svg {
    margin-right: 1rem;
  }

  &.active {
    border-left: 4px solid #3f51b5;
    padding-left: calc(1rem - 4px); /* Adjust padding to account for border width */
  }
`;

const Label = styled.span`
  color: #111111;
  font-size: 15px;
  font-family: Poppins;
  font-weight: 400;
  word-wrap: break-word;
`;
